import { useMemo, useState } from "react";
import { ApiClientError } from "../../lib/api/client";
import { uniqueProgrammeGroups } from "../../lib/group-settings-units";
import { useCreateSettingsUnitMutation, useSettingsUnitsQuery } from "../../lib/api/settings";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";

interface AddUnitModalProps {
  open: boolean;
  onClose: () => void;
}

export function AddUnitModal({ open, onClose }: AddUnitModalProps) {
  const units = useSettingsUnitsQuery(open);
  const createUnit = useCreateSettingsUnitMutation();
  const groupOptions = useMemo(
    () => uniqueProgrammeGroups(units.data?.units),
    [units.data?.units],
  );

  const [groupId, setGroupId] = useState("");
  const [name, setName] = useState("");
  const [budget, setBudget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selectedGroupId = groupId || groupOptions[0]?.id || "";

  async function save() {
    setError(null);
    const parsed = Number.parseFloat(budget);
    if (!name.trim() || !selectedGroupId || !Number.isFinite(parsed) || parsed < 0) {
      setError("Enter a unit name, parent programme, and valid budget.");
      return;
    }

    const code = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 12);

    try {
      await createUnit.mutateAsync({
        groupId: selectedGroupId,
        name: name.trim(),
        code: code || "unit",
        annualBudget: parsed.toFixed(2),
      });
      setName("");
      setBudget("");
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not create unit.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add programme unit"
      description="Create a unit beneath an existing programme."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={createUnit.isPending} onClick={() => void save()}>
            Create unit
          </Button>
        </>
      }
    >
      {error ? <p className="edlab-error mb-4">{error}</p> : null}
      <div className="form-grid">
        <label className="form-field">
          Unit name
          <input
            className="edlab-input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </label>
        <label className="form-field">
          Parent programme
          <select
            className="edlab-select"
            value={selectedGroupId}
            onChange={(event) => {
              setGroupId(event.target.value);
            }}
          >
            {groupOptions.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field form-field-full">
          Annual budget · PKR
          <input
            type="number"
            min="0"
            step="0.01"
            className="edlab-input"
            value={budget}
            onChange={(event) => {
              setBudget(event.target.value);
            }}
          />
        </label>
      </div>
      <p className="mt-4 text-xs text-edlab-muted">
        New units are granted to Super Admin only. Assign other users through People &amp; access
        in Settings.
      </p>
    </Modal>
  );
}
